import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import api from '../../../utils/api';

export interface ScholarshipApplication {
  id: number;
  applicantId: string;
  applicationNumber: string;
  applicantName: string;
  originalFileName: string;
  filePath: string;
  status: number;
  statusLabel: string;
  submittedAt: string;
  scholarshipPercent: number | null;
  programme: string | null;
  batchName: string | null;
  hasUploadedAcceptance: boolean;
  acceptanceOriginalFileName: string | null;
}

export interface ScholarshipApplicationPagination {
  data: ScholarshipApplication[];
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  isFirstPage: boolean;
  isLastPage: boolean;
  nextPage: number | null;
  previousPage: number | null;
  isSuccessful: boolean;
  message: string;
}

export interface ScholarshipApplicationResponse {
  statusCode: number;
  message: string;
  data: ScholarshipApplicationPagination;
}

export const GET_ALL_SCHOLARSHIP_APPLICATION_QUERY_KEY = ['get-all-scholarship-application'];

export const useGetAllScholarshipApplicationQuery = (page = 1, pageSize = 10) => {
  const fetchAllScholarshipApplications = async (): Promise<ScholarshipApplicationResponse> => {
    return (await api.get(`/admin/scholarship?Page=${page}&PageSize=${pageSize}`))?.data;
  };

  const { data, isLoading, isError, error, refetch } = useQuery<
    ScholarshipApplicationResponse,
    AxiosError<ApiErrorResponse>
  >({
    queryKey: [...GET_ALL_SCHOLARSHIP_APPLICATION_QUERY_KEY, page, pageSize],
    queryFn: fetchAllScholarshipApplications,
  });

  const errorMessage =
    error?.response?.data?.message || error?.message || 'Failed to fetch scholarship applications';

  return {
    data,
    scholarshipApplicationsData: data?.data?.data ?? [],
    scholarshipApplicationPagination: data?.data,
    totalRecords: data?.data?.totalRecords ?? 0,
    currentPage: data?.data?.currentPage ?? page,
    pageSize: data?.data?.pageSize ?? pageSize,
    totalPages: data?.data?.totalPages ?? 0,
    isLoading,
    isScholarshipApplicationsLoading: isLoading,
    isError,
    isScholarshipApplicationsError: isError,
    errorMessage,
    scholarshipApplicationsErrorMessage: errorMessage,
    refetch,
  };
};
